import React, { ReactElement, useState } from 'react';
import { strings } from 'src/localization';
import { ROService, VehicleRec } from 'src/models';
import classNames from 'classnames';
import * as css from './RepairService.module.scss';
import { IconCheckMark } from 'src/content/icons';
import MoreDetails from '../../MoreDetails/containers/MoreDetails';

export interface IProps {
    service: ROService;
    theme: string;
    vehicles: VehicleRec[];
    selectedVehicle?: string;
}

// Values for service status - Pick sends translated values based on the server language.
// In Progress
const statusInProgressEN = `In Progress`;
const statusInProgressFR = `En cours`;
// Finished
const statusFinishedEN = `Finished`;
const statusFinishedFR = `Terminé`;

const RepairService = (props: IProps) => {
    const [detailsOpen, setDetailsOpen] = useState(false);

    // Status will be returned from the server in the language of the server, which may not be the same as the
    // language of the client.  We need to check for both the English and French versions of the status returned from
    // Pick, then display the status in the language currently toggled, which is currently controlled by locale.
    // Not Started will be handled as the default case.
    const statusInProgress = props.service.status === statusInProgressEN || props.service.status === statusInProgressFR;
    const statusFinished = props.service.status === statusFinishedEN || props.service.status === statusFinishedFR;

    let priceLow = '';
    let priceHigh = '';
    if (props.service.warranty) {
        priceLow = strings.Warranty;
    } else if (props.service.priceLow) {
        // Format each price into the standard USD currency format.
        priceLow = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(props.service.priceLow / 100);
        if (props.service.priceHigh) {
            priceHigh =
                new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(props.service.priceHigh / 100);
        }
    }

    const opPriceStyles = classNames(
        css.opPrice,
        {
            [css.opPriceBMW]: props.theme === 'bmw',
            [css.opPriceFord]: props.theme === 'ford',

        },
    );

    const currentStatusLitStyles = classNames(
        css.currentStatusLit,
        {
            [css.currentStatusLitBMW]: props.theme === 'bmw',
            [css.currentStatusLitFord]: props.theme === 'ford',

        },
    );

    const causeLitStyles = classNames(
        css.causeLit,
        {
            [css.causeLitBMW]: props.theme === 'bmw',
            [css.causeLitFord]: props.theme === 'ford',

        },
    );
    const concernLitStyles = classNames(
        css.concernLit,
        {
            [css.concernLitBMW]: props.theme === 'bmw',
            [css.concernLitFord]: props.theme === 'ford',

        },
    );

    const correctionLitStyles = classNames(
        css.correctionLit,
        {
            [css.correctionLitBMW]: props.theme === 'bmw',
            [css.correctionLitFord]: props.theme === 'ford',

        },
    );

    // Display different circles dependent on the status of the job
    let circleClass = '';
    let finished = false;
    let status = props.service.status;

    if (statusFinished) {
        finished = true;
        circleClass = css.circleFinished;
        status = strings.Complete;
    } else if (statusInProgress) {
        status = strings.StatusInProgress;
        circleClass = css.circleInProgress;
    } else {
        status = strings.StatusNotStarted;
        circleClass = css.circleNotStarted;
    }
    // let checkMarkCSS = css.check;
    // if (props.theme === 'colorSensitive' || props.theme === 'chrysler' || props.theme === 'volkswagen') {
    //     checkMarkCSS = css.checkAccess;
    // }

    let cause: ReactElement | null = null;
    if (props.service.cause) {
        cause =
            <div>
                <div className={causeLitStyles}>{strings.Cause}</div>
                <div className={css.cause}>{props.service.cause}</div>
            </div>;
    }
    let concern: ReactElement | null = null;
    if (props.service.concern) {
        concern =
            <div>
                <div className={concernLitStyles}>{strings.Concern}</div>
                <div className={css.concern}>{props.service.concern}</div>
            </div>;
    }
    let correction: ReactElement | null = null;
    if (props.service.correction) {
        correction =
            <div>
                <div className={correctionLitStyles}>{strings.Correction}</div>
                <div className={css.correction}>{props.service.correction}</div>
            </div>;
    }
    let moreDetails: ReactElement | null = null;
    if (props.service.cause || props.service.concern || props.service.correction) {
        moreDetails =
            <div className={css.moreDetails}>
                <MoreDetails
                    headerLit={strings.OperationDetails}
                    // message is needed to suppress the services button
                    message='MENUS'
                    // Override the openFlag/setOpenFlag
                    openFlag={detailsOpen}
                    setOpenFlag={setDetailsOpen}
                    vehicles={props.vehicles}
                    selectedVehicle={props.selectedVehicle}
                >
                    <div>
                        <div>
                            <h4 id='h4LightBox'>
                                {props.service.title}
                            </h4>
                        </div>
                        <div className={opPriceStyles}>
                            {priceLow + (priceHigh ? ' - ' + priceHigh : '')}
                        </div>
                        <div>
                            <div className={currentStatusLitStyles}>
                                {strings.CurrentStatus}
                            </div>
                            <div className={css.opStatus}>
                                <div className={circleClass}>{finished ? <IconCheckMark className={css.check} /> : null}</div>
                                <div className={css.serviceStatus}>{status}</div>
                            </div>
                        </div>
                        {cause}
                        {concern}
                        {correction}
                    </div>
                </MoreDetails>
            </div>;
    }

    const serviceTitleClasses = classNames(
        css.title,
        // If more details is defined, give the cursor a pointer style on mouseover
        {
            [css.link]: !!moreDetails,
            [css.linkBMW]: !!moreDetails && props.theme === 'bmw',
            [css.titleCV]: props.theme === 'chevrolet',
            [css.titleBMW]: props.theme === 'bmw',
            [css.titleFord]: props.theme === 'ford',

        },
    );

    const servicePriceClasses = classNames(
        css.price,
        // If more details is defined, give the cursor a pointer style on mouseover
        {
            [css.link]: !!moreDetails,
            [css.priceCV]: props.theme === 'chevrolet',
            [css.linkBMW]: !!moreDetails && props.theme === 'bmw',
            [css.priceBMW]: props.theme === 'bmw',
            [css.priceFord]: props.theme === 'ford',

        },
    );

    return (
        <div className={css.currentRepairCard}>
            <div className={css.flexBox}>
                <p id='serviceTitle' className={serviceTitleClasses} onClick={() => setDetailsOpen(true)}>{props.service.title}</p>
                <p id='servicePrice' className={servicePriceClasses} onClick={() => setDetailsOpen(true)}>
                    {priceLow + (priceHigh ? ' - ' + priceHigh : '')}
                </p>
            </div>
            <div className={css.status}>
                <div className={circleClass}>{finished ? <IconCheckMark className={css.check} /> : null}</div>
                <div className={css.serviceStatus}>{status}</div>
            </div>
            {moreDetails}
            {/* <div className={css.moreDetails}>
                <MoreDetails
                    headerLit={strings.OperationDetails}
                    // message is needed to suppress the services button
                    message={'MENUS'}>
                    <div>
                        <div>
                            <h4 id='h4LightBox'>{props.service.title}</h4>
                        </div>
                        <div className={css.opPrice}>
                            {priceLow + (priceHigh ? ' - ' + priceHigh : '')}
                        </div>
                        <div>
                            <div className={css.currentStatusLit}>
                                {strings.CurrentStatus}
                            </div>
                            <div className={css.opStatus}>
                                <div className={circleClass}>{finished ? <IconCheckMark className={css.check} /> : null}</div>
                                <div className={css.serviceStatus}>{status}</div>
                            </div>
                        </div>
                        {cause}
                        {concern}
                        {correction}
                    </div>
                </MoreDetails>
            </div> */}
        </div>
    );
};


export default RepairService;
